import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Anchor, ArrowLeft, ArrowLeftRight, Check, Fingerprint, FlaskConical, GripVertical, Plus, Rocket, ShieldCheck, Trash2, X } from 'lucide-react';
import { Badge, Button, Checkbox, Section, TextField } from '@meridian/ui';
import { getAssertion, passkeySupported, type AssertionOptionsJSON, type AssertionResponseJSON } from './webauthn.js';
import './ceremony.css';

/* The promotion ceremony, extracted whole from the Console in the Ops V2
   round so one implementation is rendered by both hosts. The Console passes
   its mission client, its toast and its loading state; Ops passes a client
   that reaches the same endpoints through the daemon's mission proxy with a
   real Console owner session. Every class name, data-testid, step, gate and
   word is the Console's own — the specs that lock the ceremony address them
   and keep passing unchanged. The server-side law is untouched by design:
   this file only ever talks to the same two endpoints the Console always
   called. */

export interface CeremonyPreview {
  candidate: { version: string; gitSha: string };
  /* Which second factor the server will accept for THIS operator: a fresh
     passkey assertion once any credential is registered, TOTP only until
     then (V3 §4). */
  secondFactor?: 'passkey' | 'totp';
  fromVersion: string | null;
  waves: Array<{
    name: string;
    holdMinutes: number;
    tenants: Array<{ tenantId: string; slug: string; displayName: string; isSandbox?: boolean }>;
  }>;
  pinned: Array<{ displayName: string; pinnedVersion?: string | null }>;
  destructiveMigrations: Array<{ file: string; statements: string[]; reason?: string | null }>;
  backupPlan: { location: string };
  sandboxPresent: boolean;
  /* Countries with no canary of their own get one picked, and the ceremony
     says which and why rather than letting a pick pass unread. */
  autoPickedCanaries?: Array<{ tenantId: string; slug: string; displayName: string; countryKey: string; why: string }>;
  countriesLive?: string[];
}

export interface CeremonyClient {
  promotionPreview(candidateId: string): Promise<CeremonyPreview>;
  promotionChallenge(candidateId: string): Promise<{ options: AssertionOptionsJSON; challengeId: string }>;
  promoteRelease(
    candidateId: string,
    body: {
      confirmationVersion: string;
      checklist: { testedOnStaging: true; notesReviewed: true; migrationsReviewed: true; rollbackUnderstood: true };
      wavePlan: Array<{ name: string; holdMinutes: number; tenantIds: string[] }>;
      proof: {
        currentPassword: string;
        code?: string;
        assertion?: { challengeId: string; response: AssertionResponseJSON };
      };
    },
  ): Promise<{ rolloutId: string; version?: string }>;
}

export type CeremonyNotify = (message: string, opts?: { tone?: 'error'; detail?: string }) => void;

type WaveDraft = {
  name: string;
  holdMinutes: number;
  tenants: Array<{ tenantId: string; slug: string; displayName: string; isSandbox?: boolean }>;
};

const CHECKLIST = [
  {
    key: 'testedOnStaging' as const,
    label: 'I tested this release on staging',
    detail: 'The exact artifact has been walked on console.staging.meridian.dhs.africa: the same SHA, the same checksum.',
  },
  {
    key: 'notesReviewed' as const,
    label: 'The release notes are reviewed',
    detail: 'They describe what hospitals will receive and read correctly in the release record.',
  },
  {
    key: 'migrationsReviewed' as const,
    label: 'The database migrations are reviewed',
    detail: 'Additive by law. Anything destructive is shown in red on this page and was deliberately accepted.',
  },
  {
    key: 'rollbackUnderstood' as const,
    label: 'I know the way back',
    detail: 'Every database gets a verified snapshot before it migrates; rollback is one guarded action, per hospital or fleet-wide.',
  },
];

/* The deliberately heavy path to production. Three steps: see exactly
   what will happen, attest to the checklist, then arm with the typed
   version and a fresh password + TOTP proof. */
export function PromotionCeremony({
  candidateId,
  onBack,
  onStarted,
  client,
  notify,
  loading,
}: {
  candidateId: string;
  onBack: () => void;
  onStarted: (rolloutId: string) => void;
  client: CeremonyClient;
  notify: CeremonyNotify;
  loading?: ReactNode;
}) {
  const [preview, setPreview] = useState<CeremonyPreview | null>(null);
  const [step, setStep] = useState(1);
  const [waves, setWaves] = useState<WaveDraft[]>([]);
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  /* Hold-to-arm (V3 §4): the version is displayed large; a press-and-hold
     confirm replaces the transcription test. 0..1 while held, armed at 1. */
  const [armed, setArmed] = useState(false);
  const [holdPct, setHoldPct] = useState(0);
  const holdTimer = useRef<number | null>(null);
  const holdStart = useRef(0);
  /* The fresh assertion, held until Run consumes it. Single-use: a failed
     run clears it so the next attempt confirms again. */
  const [assertion, setAssertion] = useState<{ challengeId: string; response: AssertionResponseJSON } | null>(null);
  const [asserting, setAsserting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  /* The touch path: HTML5 drag events never fire on iOS Safari or Android
     Chrome, so a phone re-plans waves in two taps — Move… on a hospital,
     then Move here on the wave it belongs in. Drag stays for the desk. */
  const [moving, setMoving] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const loaded = await client.promotionPreview(candidateId);
        setPreview(loaded);
        setWaves(loaded.waves.map((wave) => ({ name: wave.name, holdMinutes: wave.holdMinutes, tenants: wave.tenants })));
      } catch (error) {
        notify('The promotion preview could not be loaded', { tone: 'error', detail: error instanceof Error ? error.message : undefined });
        onBack();
      }
    })();
  }, [candidateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const allChecked = CHECKLIST.every((item) => checks[item.key]);
  const hospitalCount = useMemo(() => waves.reduce((sum, wave) => sum + wave.tenants.length, 0), [waves]);

  if (!preview) {
    return <>{loading ?? <p className="msn-ceremony__loading">Preparing the ceremony…</p>}</>;
  }

  function moveTenant(tenantId: string, toWave: number) {
    setWaves((current) => {
      const from = current.findIndex((wave) => wave.tenants.some((tenant) => tenant.tenantId === tenantId));
      if (from === -1 || from === toWave) return current;
      const tenant = current[from].tenants.find((entry) => entry.tenantId === tenantId)!;
      /* The sandbox may get company in Wave 0, but it never leaves it. */
      if (tenant.isSandbox) return current;
      return current.map((wave, index) => ({
        ...wave,
        tenants:
          index === from
            ? wave.tenants.filter((entry) => entry.tenantId !== tenantId)
            : index === toWave
              ? [...wave.tenants, tenant]
              : wave.tenants,
      }));
    });
  }

  function addWave() {
    setWaves((current) => [...current, { name: `Wave ${current.length} (Custom)`, holdMinutes: 0, tenants: [] }]);
  }

  function removeWave(index: number) {
    setWaves((current) => {
      if (current.length <= 1 || current[index].tenants.length > 0) return current;
      return current.filter((_, i) => i !== index);
    });
  }

  /* V4 hotfix: only use the passkey path when the server EXPLICITLY says
     so. The previous default (anything not literally 'totp') meant a
     pre-V3 Console that doesn't return the field at all was read as
     wanting a passkey — the client then asked for /promotion-challenge
     (a V3-added endpoint), got a 404 "Cannot POST", and surfaced it as
     "The passkey was not confirmed". Pre-V3 Console works with TOTP,
     V3+ Console tells the client explicitly which factor to collect —
     this default is safe both ways. */
  const wantsPasskey = preview?.secondFactor === 'passkey';

  async function confirmPasskey() {
    setAsserting(true);
    try {
      const { options, challengeId } = await client.promotionChallenge(candidateId);
      const response = await getAssertion(options);
      setAssertion({ challengeId, response });
    } catch (error) {
      notify('The passkey was not confirmed', { tone: 'error', detail: error instanceof Error ? error.message : undefined });
    } finally {
      setAsserting(false);
    }
  }

  /* Press-and-hold: pointer or keyboard (hold Enter or Space). Releasing
     early disarms the attempt; a full hold arms. */
  const HOLD_MS = 1200;
  function holdBegin() {
    if (armed || busy) return;
    holdStart.current = Date.now();
    const tick = () => {
      const pct = Math.min(1, (Date.now() - holdStart.current) / HOLD_MS);
      setHoldPct(pct);
      if (pct >= 1) {
        setArmed(true);
        holdTimer.current = null;
        return;
      }
      holdTimer.current = window.setTimeout(tick, 40);
    };
    tick();
  }
  function holdEnd() {
    if (holdTimer.current !== null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    if (!armed) setHoldPct(0);
  }

  async function run() {
    setBusy(true);
    try {
      const result = await client.promoteRelease(candidateId, {
        /* The version the operator saw and held to arm — displayed large
           above; the server still refuses anything but the exact string. */
        confirmationVersion: preview!.candidate.version,
        checklist: { testedOnStaging: true, notesReviewed: true, migrationsReviewed: true, rollbackUnderstood: true },
        wavePlan: waves
          .filter((wave) => wave.tenants.length > 0)
          .map((wave) => ({ name: wave.name, holdMinutes: wave.holdMinutes, tenantIds: wave.tenants.map((tenant) => tenant.tenantId) })),
        proof: assertion
          ? { currentPassword: password, assertion }
          : { currentPassword: password, code: code.trim() },
      });
      notify(`Release ${preview!.candidate.version} approved. The rollout is starting`, {
        detail: 'Wave 0 begins with the demo sandbox. Watch the board.',
      });
      onStarted(result.rolloutId);
    } catch (error) {
      /* Challenges and assertions are single-use; a refused run means the
         next attempt confirms the passkey again. */
      setAssertion(null);
      notify('The release was not armed', { tone: 'error', detail: error instanceof Error ? error.message : undefined });
    } finally {
      setBusy(false);
    }
  }

  const destructive = preview.destructiveMigrations;

  return (
    <div className="msn-ceremony" data-testid="promotion-ceremony">
      <div className="msn-ceremony__masthead">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft size={14} /> Back to the release
        </Button>
        <div className="msn-ceremony__title">
          <ShieldCheck size={20} />
          <div>
            <h2>Promote {preview.candidate.version} to the fleet</h2>
            <p>
              {preview.fromVersion ? `The fleet runs ${preview.fromVersion}.` : 'The fleet runs its pre-release build.'} This ceremony is the only
              path to hospitals. Nothing else can reach them.
            </p>
          </div>
        </div>
        <ol className="msn-ceremony__steps">
          {['What moves', 'The attestation', 'Arm and run'].map((label, index) => (
            <li key={label} className={step === index + 1 ? 'is-active' : step > index + 1 ? 'is-done' : ''}>
              <span>{step > index + 1 ? <Check size={12} /> : index + 1}</span> {label}
            </li>
          ))}
        </ol>
      </div>

      {step === 1 && (
        <>
          <div className="msn-ceremony__facts">
            <article>
              <small>Release</small>
              <strong>{preview.candidate.version}</strong>
              <span className="mrd-num">{preview.candidate.gitSha.slice(0, 12)}</span>
            </article>
            <article>
              <small>Hospitals affected</small>
              <strong>{hospitalCount}</strong>
              <span>{preview.pinned.length > 0 ? `${preview.pinned.length} pinned, deliberately skipped` : 'none pinned'}</span>
            </article>
            <article>
              <small>From → to</small>
              <strong>
                {preview.fromVersion ?? 'pre-release'} → {preview.candidate.version}
              </strong>
              <span>one hospital at a time</span>
            </article>
            <article>
              <small>Backups</small>
              <strong>one verified snapshot per database</strong>
              <span>{preview.backupPlan.location}</span>
            </article>
          </div>

          {destructive.length > 0 ? (
            <div className="msn-destructive-block">
              <div className="msn-destructive-block__head">
                <AlertTriangle size={16} />
                <strong>This release contains destructive database statements</strong>
              </div>
              {destructive.map((entry) => (
                <article key={entry.file}>
                  <header className="mrd-num">{entry.file}</header>
                  {entry.statements.map((statement, index) => (
                    <pre key={index}>{statement}</pre>
                  ))}
                  <footer>{entry.reason ? `Marked allowed: ${entry.reason}` : 'No allow marker recorded.'}</footer>
                </article>
              ))}
            </div>
          ) : (
            <div className="msn-ceremony__safe">
              <Check size={15} /> No destructive migrations. Databases only gain; rolling back stays a pure route flip.
            </div>
          )}

          <Section title="The wave plan" meta="drag hospitals between waves, or tap Move… · the demo sandbox always goes first">
            {!preview.sandboxPresent && (
              <div className="msn-ceremony__warning">
                <AlertTriangle size={14} /> No demo sandbox exists on this host. Wave 0 is empty. The first real hospital will be the first to
                meet this release.
              </div>
            )}
            {(preview.autoPickedCanaries ?? []).map((pick) => (
              <div className="msn-ceremony__warning" key={pick.tenantId}>
                <AlertTriangle size={14} /> {pick.displayName} goes into Wave 1 as {pick.countryKey}&rsquo;s canary. {pick.why} Flag a canary on
                that country&rsquo;s own hospital to choose a different one.
              </div>
            ))}
            <div className="msn-wave-editor">
              {waves.map((wave, index) => (
                <div
                  key={index}
                  className={`msn-wave ${dragging ? 'is-drop-target' : ''}`}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (dragging) moveTenant(dragging, index);
                    setDragging(null);
                  }}
                >
                  <header>
                    <input
                      className="msn-wave__name"
                      value={wave.name}
                      onChange={(event) => {
                        const value = event.target.value;
                        setWaves((current) => current.map((entry, i) => (i === index ? { ...entry, name: value } : entry)));
                      }}
                    />
                    <label className="msn-wave__hold">
                      hold
                      <input
                        type="number"
                        min={0}
                        max={1440}
                        value={wave.holdMinutes}
                        onChange={(event) => {
                          const value = Math.max(0, Math.min(1440, Number(event.target.value) || 0));
                          setWaves((current) => current.map((entry, i) => (i === index ? { ...entry, holdMinutes: value } : entry)));
                        }}
                      />
                      min after
                    </label>
                    {moving && !wave.tenants.some((tenant) => tenant.tenantId === moving.id) && (
                      <button
                        type="button"
                        className="msn-wave__movehere"
                        data-testid={`wave-movehere-${index}`}
                        onClick={() => {
                          moveTenant(moving.id, index);
                          setMoving(null);
                        }}
                      >
                        <ArrowLeftRight size={12} /> Move {moving.name} here
                      </button>
                    )}
                    {wave.tenants.length === 0 && waves.length > 1 && !moving && (
                      <button type="button" className="msn-wave__remove" onClick={() => removeWave(index)} aria-label="Remove empty wave">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </header>
                  {wave.tenants.length === 0 ? (
                    <p className="msn-wave__empty">{moving ? 'Tap “Move here” above' : 'Drag hospitals here, or tap Move… on one'}</p>
                  ) : (
                    <ul>
                      {wave.tenants.map((tenant) => (
                        <li
                          key={tenant.tenantId}
                          draggable={!tenant.isSandbox}
                          onDragStart={() => setDragging(tenant.tenantId)}
                          onDragEnd={() => setDragging(null)}
                          className={tenant.isSandbox ? 'is-locked' : ''}
                        >
                          {tenant.isSandbox ? <FlaskConical size={13} /> : <GripVertical size={13} />}
                          <span>{tenant.displayName}</span>
                          <small>{tenant.slug}</small>
                          {tenant.isSandbox && <Badge variant="brand">always first</Badge>}
                          {!tenant.isSandbox && (
                            <button
                              type="button"
                              className="msn-wave__move"
                              data-testid={`wave-move-${tenant.slug}`}
                              aria-pressed={moving?.id === tenant.tenantId}
                              onClick={() =>
                                setMoving((current) =>
                                  current?.id === tenant.tenantId ? null : { id: tenant.tenantId, name: tenant.displayName },
                                )
                              }
                            >
                              {moving?.id === tenant.tenantId ? <X size={12} /> : <ArrowLeftRight size={12} />}
                              {moving?.id === tenant.tenantId ? 'Cancel' : 'Move…'}
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              <button type="button" className="msn-wave-add" onClick={addWave}>
                <Plus size={14} /> Add a wave
              </button>
            </div>
            {preview.pinned.length > 0 && (
              <div className="msn-ceremony__pinned">
                <Anchor size={13} />
                <span>
                  Skipped (pinned):{' '}
                  {preview.pinned.map((tenant) => `${tenant.displayName}${tenant.pinnedVersion ? ` @ ${tenant.pinnedVersion}` : ''}`).join(' · ')}
                </span>
              </div>
            )}
          </Section>

          <div className="msn-ceremony__nav">
            <span />
            <Button variant="primary" onClick={() => setStep(2)} disabled={hospitalCount === 0}>
              Continue to the attestation
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <Section title="The attestation" meta="each line is yours to mean, not to skim">
            <div className="msn-ceremony__checklist">
              {CHECKLIST.map((item) => (
                <label key={item.key} className={checks[item.key] ? 'is-checked' : ''}>
                  <Checkbox
                    label=""
                    checked={Boolean(checks[item.key])}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setChecks((current) => ({ ...current, [item.key]: checked }));
                    }}
                  />
                  <span>
                    <strong>{item.label}</strong>
                    <small>{item.detail}</small>
                  </span>
                </label>
              ))}
            </div>
          </Section>
          <div className="msn-ceremony__nav">
            <Button variant="secondary" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button variant="primary" onClick={() => setStep(3)} disabled={!allChecked}>
              Continue to arm
            </Button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <Section
            title="Arm the release"
            meta={wantsPasskey ? 'hold to arm, then your password and your passkey' : 'hold to arm, then your password and a fresh authenticator code'}
          >
            <div className="msn-ceremony__arm">
              <p>
                This is the version that will reach every hospital. Hold the button to arm it; the Run button then starts Wave 0
                immediately. The demo sandbox moves first, then the waves you planned, each hospital behind its own verified snapshot.
              </p>
              <p data-testid="arm-restarts">
                The promotion restarts each serving copy once, one after the other, while the other serves. During the clinics&apos;
                hours one copy then carries every hospital for a minute or two, so press after the day&apos;s peak where you can.
              </p>
              <div className="msn-ceremony__version" data-testid="arm-version">
                <span className="msn-ceremony__version-label">Arming</span>
                <strong>{preview.candidate.version}</strong>
                <span className="msn-ceremony__version-sha">{preview.candidate.gitSha.slice(0, 12)}</span>
              </div>
              <button
                type="button"
                className={`msn-ceremony__holdarm${armed ? ' is-armed' : ''}`}
                data-testid="hold-to-arm"
                aria-pressed={armed}
                disabled={busy}
                onPointerDown={(event) => {
                  event.preventDefault();
                  holdBegin();
                }}
                onPointerUp={holdEnd}
                onPointerLeave={holdEnd}
                onPointerCancel={holdEnd}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) {
                    event.preventDefault();
                    holdBegin();
                  }
                }}
                onKeyUp={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') holdEnd();
                }}
              >
                <span className="msn-ceremony__holdarm-fill" style={{ width: `${Math.round((armed ? 1 : holdPct) * 100)}%` }} aria-hidden />
                <span className="msn-ceremony__holdarm-text">
                  {armed ? (
                    <>
                      <Check size={14} /> Armed: {preview.candidate.version}
                    </>
                  ) : (
                    `Hold to arm ${preview.candidate.version}`
                  )}
                </span>
              </button>
              <div className="msn-ceremony__proof">
                <TextField label="Your password" type="password" value={password} onChange={setPassword} autoComplete="current-password" />
                {wantsPasskey ? (
                  <div className="msn-ceremony__passkey">
                    <span className="msn-ceremony__passkey-label">Passkey</span>
                    {assertion ? (
                      <span className="msn-ceremony__passkey-done" data-testid="passkey-confirmed">
                        <Check size={14} /> Confirmed with your passkey
                      </span>
                    ) : (
                      <Button
                        variant="secondary"
                        onClick={() => void confirmPasskey()}
                        loading={asserting}
                        disabled={busy || !passkeySupported()}
                      >
                        <Fingerprint size={14} /> Confirm with your passkey
                      </Button>
                    )}
                    {!passkeySupported() ? (
                      <span className="msn-ceremony__passkey-note">This browser has no passkey support.</span>
                    ) : null}
                  </div>
                ) : (
                  <TextField label="Authenticator code" value={code} onChange={setCode} autoComplete="one-time-code" />
                )}
              </div>
            </div>
          </Section>
          <div className="msn-ceremony__nav">
            <Button
              variant="secondary"
              onClick={() => {
                setStep(2);
                setArmed(false);
                setHoldPct(0);
              }}
              disabled={busy}
            >
              Back
            </Button>
            <Button
              variant="danger"
              loading={busy}
              disabled={!armed || password.length === 0 || (wantsPasskey ? !assertion : code.trim().length < 6)}
              onClick={() => void run()}
            >
              <Rocket size={14} /> Run the rollout
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
